import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { expandWidget } from '@/utils/sonaxWidgetControl';
import { IncomingCallDrawer } from '@/components/sonax/IncomingCallDrawer';

/**
 * Extrai a sequência que MAIS parece um telefone BR (10–13 dígitos) de um texto.
 * A Sonax renderiza "Nome - <número>" (toque) e "Nome / número" (em ligação), então
 * não dá pra confiar no texto cru. Letras quebram as runs, então o nome não é
 * concatenado ao número. Preferimos a run mais longa (com DDI).
 */
function pickPhoneFromText(s: string): string | null {
  const runs = (s || '').match(/[\d\s().+-]{8,}/g);
  if (!runs) return null;
  const candidates = runs
    .map((r) => r.replace(/\D/g, ''))
    .filter((d) => d.length >= 10 && d.length <= 13);
  if (!candidates.length) return null;
  return candidates.sort((a, b) => a.length - b.length).pop() ?? null;
}

interface SonaxWidgetContextType {
  isWidgetVisible: boolean;
  toggleWidget: () => void;
  showWidget: () => void;
  hideWidget: () => void;
}

const SonaxWidgetContext = createContext<SonaxWidgetContextType | null>(null);

export const useSonaxWidgetContext = () => {
  const context = useContext(SonaxWidgetContext);
  if (!context) {
    throw new Error('useSonaxWidgetContext must be used within SonaxWidgetProvider');
  }
  return context;
};

interface SonaxWidgetProviderProps {
  children: React.ReactNode;
}

export const SonaxWidgetProvider: React.FC<SonaxWidgetProviderProps> = ({ children }) => {
  const [isWidgetVisible, setIsWidgetVisible] = useState(true); // Inicia como visível por padrão
  const [userDisabled, setUserDisabled] = useState(false); // Flag para indicar se o usuário desativou explicitamente
  const [hasInitialized, setHasInitialized] = useState(false); // Flag para indicar se já inicializou

  // ============================================================================
  // Estado do drawer de chamada recebida (UI lateral com lookup do contato)
  // ============================================================================
  const [incomingDrawerOpen, setIncomingDrawerOpen] = useState(false);
  const [incomingPhone, setIncomingPhone] = useState<string | null>(null);

  const hideWidget = useCallback(() => {
    setIsWidgetVisible(false);
    setUserDisabled(true); // Marca que o usuário desativou explicitamente
    
    // Limpa o interval de monitoramento
    if ((window as any).__sonaxVisibilityInterval) {
      clearInterval((window as any).__sonaxVisibilityInterval);
      (window as any).__sonaxVisibilityInterval = null;
    }
    
    // Aplica estilos para esconder o widget E desabilitar pointer-events
    const sonaxElements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');
    sonaxElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement) {
        // Ignora o campo do formulário de configurações, os campos de config Sonax e o
        // botão de softphone do header (componente do CRM, casa com [class*="sonax"]).
        if (htmlElement.id === 'sonax_widget_script' ||
            htmlElement.id?.includes('sonax_widget_script') ||
            htmlElement.id === 'id_ligacao' ||
            htmlElement.id === 'senha_ligacao' ||
            htmlElement.classList.contains('sonax-header-btn')) {
          return;
        }
        htmlElement.style.display = 'none';
        htmlElement.style.setProperty('pointer-events', 'none', 'important');
        htmlElement.removeAttribute('data-sonax-visible');

        // Preserva estilos de posicionamento para que expandWidget() nao precise reaplicar tudo

        // Desabilita pointer-events em TODOS os elementos filhos e descendentes
        const allChildren = htmlElement.querySelectorAll('*');
        allChildren.forEach((child) => {
          const childElement = child as HTMLElement;
          if (childElement) {
            childElement.style.setProperty('pointer-events', 'none', 'important');
          }
        });
      }
    });
  }, []);

  const showWidget = useCallback(() => {
    setIsWidgetVisible(true);
    setUserDisabled(false); // Marca que o usuário ativou explicitamente
    
    // Aplica estilos para mostrar o widget
    const sonaxElements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');
    sonaxElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement) {
        // Ignora o campo do formulário de configurações e o botão de softphone do header.
        if (htmlElement.id === 'sonax_widget_script' ||
            htmlElement.id?.includes('sonax_widget_script') ||
            htmlElement.classList.contains('sonax-header-btn')) {
          return;
        }
        htmlElement.style.display = '';
        htmlElement.style.visibility = '';
        htmlElement.style.opacity = '';
        htmlElement.style.setProperty('pointer-events', 'auto', 'important');
        htmlElement.setAttribute('data-sonax-visible', 'true');
        
        // Força cor preta nos inputs do telefone dentro do widget
        const phoneInputs = htmlElement.querySelectorAll('input[type="text"], input[type="tel"], input[id*="phone"], input[id*="Phone"], #phoneNumber');
        phoneInputs.forEach((input) => {
          const inputElement = input as HTMLInputElement;
          if (inputElement) {
            inputElement.style.setProperty('color', '#000000', 'important');
            inputElement.style.setProperty('-webkit-text-fill-color', '#000000', 'important');
          }
        });
        
        // Aplica também a todos os descendentes
        htmlElement.querySelectorAll('*').forEach(child => {
          const childElement = child as HTMLElement;
          childElement.style.setProperty('pointer-events', 'auto', 'important');
          
          // Se for um input, também aplica cor preta
          if (childElement.tagName === 'INPUT' && 
              ((childElement as HTMLInputElement).type === 'text' || 
               (childElement as HTMLInputElement).type === 'tel' ||
               childElement.id?.toLowerCase().includes('phone'))) {
            childElement.style.setProperty('color', '#000000', 'important');
            childElement.style.setProperty('-webkit-text-fill-color', '#000000', 'important');
          }
        });
      }
    });
    
    // Monitora continuamente para garantir que o widget permaneça visível
    const interval = setInterval(() => {
      // Se o header button está controlando, não interfere
      const headerControlled = (window as any).__sonaxWidgetOpen;
      if (headerControlled !== undefined) return;

      const elements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');
      elements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (htmlElement && htmlElement.hasAttribute('data-sonax-visible')) {
          htmlElement.style.display = '';

          // Força cor preta nos inputs do telefone dentro do widget
          const phoneInputs = htmlElement.querySelectorAll('input[type="text"], input[type="tel"], input[id*="phone"], input[id*="Phone"], #phoneNumber');
          phoneInputs.forEach((input) => {
            const inputElement = input as HTMLInputElement;
            if (inputElement) {
              inputElement.style.setProperty('color', '#000000', 'important');
              inputElement.style.setProperty('-webkit-text-fill-color', '#000000', 'important');
            }
          });
        }
      });
    }, 500);
    
    // Armazena o interval para limpar depois
    (window as any).__sonaxVisibilityInterval = interval;
  }, []);

  // Monitora quando o widget é fechado pelo próprio widget (botão X)
  useEffect(() => {
    if (!isWidgetVisible) return;

    const checkWidgetVisibility = () => {
      // Se o header button está controlando, não interfere
      const headerControlled = (window as any).__sonaxWidgetOpen;
      if (headerControlled !== undefined) return;

      const sonaxElements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');
      let hasVisibleElement = false;

      sonaxElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (htmlElement) {
          const computedStyle = window.getComputedStyle(htmlElement);
          if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden' && computedStyle.opacity !== '0') {
            hasVisibleElement = true;
          }
        }
      });

      // Se o widget estava visível mas agora não está mais, atualiza o estado
      if (!hasVisibleElement) {
        setIsWidgetVisible(false);
      }
    };

    // Verifica a cada 500ms se o widget foi fechado
    const interval = setInterval(checkWidgetVisibility, 500);

    return () => clearInterval(interval);
  }, [isWidgetVisible]);

  const toggleWidget = useCallback(() => {
    if (isWidgetVisible) {
      hideWidget();
    } else {
      showWidget();
    }
  }, [isWidgetVisible, showWidget, hideWidget]);

  // Mostra o widget automaticamente quando ele carregar (apenas na primeira vez)
  useEffect(() => {
    // Se já inicializou ou o usuário desativou explicitamente, não mostra automaticamente
    if (hasInitialized || userDisabled) return;

    // Verifica se há elementos do Sonax no DOM
    const checkAndShowWidget = () => {
      // Se o header button está controlando, não interfere
      const headerControlled = (window as any).__sonaxWidgetOpen;
      if (headerControlled !== undefined) {
        setHasInitialized(true);
        return;
      }

      const sonaxElements = document.querySelectorAll('[id*="sonax" i], [class*="sonax" i], [id*="webphone" i], [class*="webphone" i]');

      if (sonaxElements.length > 0 && !isWidgetVisible && !userDisabled && !hasInitialized) {
        // Se há elementos mas o widget não está marcado como visível, mostra
        showWidget();
        setHasInitialized(true);
      }
    };

    // Verifica imediatamente
    checkAndShowWidget();

    // Verifica periodicamente até encontrar o widget (apenas se não foi desativado pelo usuário e ainda não inicializou)
    const interval = setInterval(() => {
      if (!userDisabled && !hasInitialized) {
        checkAndShowWidget();
      }
    }, 1000);

    // Limpa o interval após 10 segundos (tempo suficiente para o widget carregar)
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setHasInitialized(true); // Marca como inicializado mesmo se não encontrou
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isWidgetVisible, showWidget, userDisabled, hasInitialized]);

  // ============================================================================
  // Auto-expand em chamada recebida.
  //
  // DOM Sonax confirmado: existe um elemento `<div class="reciving-call"
  // id="reciving-call">` SIBLING do `#widget` dentro de `.SonaxWidget`. Estado
  // normal: `style="display: none"` inline. Quando uma chamada chega, Sonax
  // muda esse estilo inline para tornar visível.
  //
  // Estratégia precisa: MutationObserver SCOPED somente no `#reciving-call`,
  // filtrando apenas mutations de `style`. Sem polling, sem busca por texto,
  // sem interferir em outros elementos. Quando detecta transição de display
  // none → visível, dispara `expandWidget()` pra que o widget principal não
  // fique escondido enquanto o ringing pulsa em bottom-right.
  // ============================================================================
  useEffect(() => {
    let lastDisplay: string | null = null;

    const computeDisplay = (el: HTMLElement): string => {
      // Prioriza inline (Sonax controla via inline style); fallback p/ computed
      return el.style.display || window.getComputedStyle(el).display || '';
    };

    const extractRingingPhone = (target: HTMLElement): string | null => {
      // #reciving-info agora vem "Nome - <número>" (antes só o número). Fallback: painel todo.
      const span = target.querySelector('#reciving-info') as HTMLElement | null;
      return pickPhoneFromText(span?.textContent || '') ?? pickPhoneFromText(target.textContent || '');
    };

    const checkRinging = (target: HTMLElement) => {
      const display = computeDisplay(target);
      // Visível = display computado != none. A Sonax alterna a visibilidade tanto por style
      // inline (versões antigas) quanto por CLASSE (versão atual — #active-call usa
      // `tab-option-hidden`, #reciving-call é escondido via stylesheet). Por isso NÃO exigimos
      // style inline: computeDisplay já prioriza inline e cai pro computed. O falso positivo
      // de boot é evitado pelo guard de "primeiro check" (lastDisplay null) + só disparar na
      // transição (display !== lastDisplay) abaixo.
      const isRinging = display !== 'none';

      // No primeiro check (lastDisplay null), apenas registra o estado SEM
      // disparar — só transições posteriores detectadas pelo observer abrem
      // o drawer. Isso evita falso positivo quando o observer anexa antes da
      // Sonax setar display:none inline (race no script initialization).
      if (lastDisplay === null) {
        lastDisplay = display;
        return;
      }

      // Só dispara na transição (mudança de valor) pra não chamar
      // expandWidget repetidamente
      if (display !== lastDisplay) {
        if (isRinging) {
          const phone = extractRingingPhone(target);
          // Bridge pro modo operar (/discadora/operar): avisa a tela. Se ela estiver no
          // comando (__sonaxOperateMode), NÃO abre o drawer global nem expande o widget.
          window.dispatchEvent(new CustomEvent('sonax:incoming-call', { detail: { phone } }));
          if ((window as any).__sonaxOperateMode) {
            lastDisplay = display;
            return;
          }
          console.log(
            '[SonaxWidget] #reciving-call ficou visível, expandindo widget. display=',
            display,
            'phone=',
            phone,
          );
          (window as any).__sonaxWidgetOpen = true;
          expandWidget();
          setTimeout(() => expandWidget(), 250);
          setTimeout(() => expandWidget(), 750);

          // Abre o drawer lateral com o lookup do contato.
          // Pequeno delay pra garantir que #reciving-info já tenha o número
          // (Sonax preenche logo antes de mostrar, mas é defensivo).
          setTimeout(() => {
            const finalPhone = phone ?? extractRingingPhone(target);
            if (finalPhone) {
              setIncomingPhone(finalPhone);
              setIncomingDrawerOpen(true);
            } else {
              console.warn('[SonaxWidget] Ringing detectado mas não consegui extrair o número.');
            }
          }, 80);
        } else {
          // O painel de ringing some tanto quando a chamada é ATENDIDA (vira #active-call)
          // quanto quando é perdida. Não é "fim de chamada" — quem decide é o observer do
          // #active-call (sonax:call-answered / sonax:call-ended).
          window.dispatchEvent(new CustomEvent('sonax:ringing-stopped'));
        }
        lastDisplay = display;
      }
    };

    // Vincula (e RE-vincula) o observer ao #reciving-call ATUAL. Necessário porque o
    // useSonaxWidget REMOVE e RE-INJETA o div.SonaxWidget ao (re)armar/navegar, recriando o
    // #reciving-call — o observer antigo ficaria preso no nó removido e pararia de disparar
    // (card não aparece até dar reload). Também cobre injeção tardia: o retry antigo desistia
    // após 30s do mount do provider, então abrir a discadora depois disso nunca anexava.
    let nodeObserver: MutationObserver | null = null;
    let currentTarget: HTMLElement | null = null;

    const bind = () => {
      const target = document.querySelector('#reciving-call') as HTMLElement | null;
      if (target === currentTarget) return; // nó não mudou — nada a fazer
      if (nodeObserver) { nodeObserver.disconnect(); nodeObserver = null; }
      currentTarget = target;
      lastDisplay = null; // re-registra o estado base do nó novo (evita falso positivo)
      if (!target) return;
      nodeObserver = new MutationObserver(() => checkRinging(target));
      // 'class' é essencial: a Sonax alterna a visibilidade por classe, não por style inline.
      nodeObserver.observe(target, { attributes: true, attributeFilter: ['style', 'class'] });
      checkRinging(target); // registra estado inicial (lastDisplay null → só grava, não dispara)
    };

    // Observer persistente no body: detecta (re)criação/remoção do #reciving-call e re-vincula.
    // Sem limite de tempo (o retry antigo de 30s desistia e perdia injeções tardias/recriações).
    const rootObserver = new MutationObserver(bind);
    rootObserver.observe(document.body, { childList: true, subtree: true });
    bind();

    return () => {
      rootObserver.disconnect();
      if (nodeObserver) nodeObserver.disconnect();
    };
  }, []);

  // ============================================================================
  // Observer do #active-call: distingue "atendida" de "encerrada".
  // O Sonax mostra `#active-call` enquanto a chamada está em curso (sibling do
  // #reciving-call). Transições do display inline:
  //   none → visível  ⇒ sonax:call-answered (a ligação foi atendida)
  //   visível → none  ⇒ sonax:call-ended    (a ligação realmente terminou)
  // A tela /discadora/operar usa esses eventos pra manter o card de contexto
  // aberto durante toda a ligação (o ringing sumir NÃO fecha o card).
  // ============================================================================
  useEffect(() => {
    let lastDisplay: string | null = null;

    const computeDisplay = (el: HTMLElement): string =>
      el.style.display || window.getComputedStyle(el).display || '';

    const checkActive = (target: HTMLElement) => {
      const display = computeDisplay(target);
      // Vide checkRinging: a Sonax alterna via classe (`tab-option-hidden`), não via style
      // inline. Baseamos "ativa" no display computado; guard de boot + transição evitam o
      // falso positivo.
      const isActive = display !== 'none';

      if (lastDisplay === null) {
        lastDisplay = display;
        return;
      }
      if (display !== lastDisplay) {
        if (isActive) {
          // Inclui o telefone (de #currPhone / texto do painel). Necessário porque a discadora
          // preditiva pode conectar o agente DIRETO em "atendida", sem fase de toque detectável
          // — nesse caso o sonax:incoming-call nunca dispara e a tela de operar precisa resolver
          // o lead a partir daqui.
          const currPhone = target.querySelector('#currPhone') as HTMLElement | null;
          const phone =
            pickPhoneFromText(currPhone?.textContent || '') ?? pickPhoneFromText(target.textContent || '');
          window.dispatchEvent(new CustomEvent('sonax:call-answered', { detail: { phone } }));
        } else {
          window.dispatchEvent(new CustomEvent('sonax:call-ended'));
        }
        lastDisplay = display;
      }
    };

    // Vide o observer do #reciving-call: re-vincula ao nó ATUAL (o widget é re-injetado) e
    // cobre injeção tardia, sem o limite de 30s do retry antigo.
    let nodeObserver: MutationObserver | null = null;
    let currentTarget: HTMLElement | null = null;

    const bind = () => {
      const target = document.querySelector('#active-call') as HTMLElement | null;
      if (target === currentTarget) return;
      if (nodeObserver) { nodeObserver.disconnect(); nodeObserver = null; }
      currentTarget = target;
      lastDisplay = null;
      if (!target) return;
      nodeObserver = new MutationObserver(() => checkActive(target));
      // 'class' é essencial: a Sonax alterna #active-call pela classe `tab-option-hidden`.
      nodeObserver.observe(target, { attributes: true, attributeFilter: ['style', 'class'] });
      checkActive(target);
    };

    const rootObserver = new MutationObserver(bind);
    rootObserver.observe(document.body, { childList: true, subtree: true });
    bind();

    return () => {
      rootObserver.disconnect();
      if (nodeObserver) nodeObserver.disconnect();
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      isWidgetVisible,
      toggleWidget,
      showWidget,
      hideWidget,
    }),
    [isWidgetVisible, toggleWidget, showWidget, hideWidget],
  );

  return (
    <SonaxWidgetContext.Provider value={contextValue}>
      {children}
      <IncomingCallDrawer
        open={incomingDrawerOpen}
        onOpenChange={(open) => {
          setIncomingDrawerOpen(open);
          if (!open) {
            // Limpa phone quando user fecha — próxima chamada vai gerar novo phone
            setIncomingPhone(null);
          }
        }}
        phone={incomingPhone}
      />
    </SonaxWidgetContext.Provider>
  );
};

